/* eslint-disable complexity */
/* eslint-disable no-param-reassign */
import { IScene3D, IShape3D } from '@src/slide/domain/ui-model/element-modeles/property/3d/property3d.interface';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { colorStrToColorDmlA, getEgColorChoiceRgba } from '@src/slide/infrastructure/module/independent/color/transform';
import { angleToEmu, emuToAngle, emuToPt, ptToEmu } from '@src/slide/infrastructure/module/independent/unit';
import { DeltaOf, ICTScene3D, ICTShape3D, STLightRigDirection, STLightRigType, STPresetCameraType, STPresetMaterialType } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';

/**
 * 3d 相关数据转换逻辑
 */
export class Element3DConverter {
    public static convertScene3DFromSdkToUi(
        sdkData?: DeepReadonly<DeepPartial<ICTScene3D>>,
        uiData?: IScene3D,
    ) {
        if (!sdkData || !uiData) {
            return;
        }
        if (sdkData.camera) {
            uiData.camera = {};
            if (sdkData.camera.prst) {
                const { prst } = sdkData.camera;
                uiData.camera.preset = STPresetCameraType[prst];
            }
            if (sdkData.camera.rot) {
                uiData.camera.rotation = {
                    x: emuToAngle(sdkData.camera.rot.lon ?? 0),
                    y: emuToAngle(sdkData.camera.rot.lat ?? 0),
                    z: emuToAngle(sdkData.camera.rot.rev ?? 0),
                };
            }
        }
        if (sdkData.lightRig) {
            uiData.lightRig = {};
            if (sdkData.lightRig.rig) {
                uiData.lightRig.rigPreset = STLightRigType[sdkData.lightRig.rig] as any;
            }
            if (sdkData.lightRig.dir) {
                uiData.lightRig.direction = STLightRigDirection[sdkData.lightRig.dir];
            }
            if (sdkData.lightRig.rot) {
                uiData.lightRig.rotation = {
                    x: emuToAngle(sdkData.lightRig.rot.lon ?? 0),
                    y: emuToAngle(sdkData.lightRig.rot.lat ?? 0),
                    z: emuToAngle(sdkData.lightRig.rot.rev ?? 0),
                };
            }
        }
    }

    public static convertShape3DFromSdkToUi(sdkData?: DeepReadonly<DeepPartial<ICTShape3D>>, uiData?: IShape3D) {
        if (!sdkData || !uiData) {
            return;
        }
        if (sdkData.prstMaterial) {
            uiData.materialPreset = STPresetMaterialType[sdkData.prstMaterial] as any;
        }
        if (sdkData.extrusionH) {
            uiData.extrusionHeight = emuToPt(sdkData.extrusionH ?? 0);
        }
        if (sdkData.extrusionClr) {
            uiData.extrusionColor = new TDColor({
                rgbaStr: getEgColorChoiceRgba(sdkData.extrusionClr.srgbClr),
            });
        }
    }

    public static convertScene3DFromUiToSdk(
        uiData?: DeepReadonly<DeepPartial<IScene3D>>,
        sdkData?: DeltaOf<ICTScene3D>,
    ) {
        if (!uiData || !sdkData) {
            return;
        }
        if (uiData.camera) {
            sdkData.camera = sdkData.camera ?? {};
            if (uiData.camera.preset) {
                sdkData.camera.prst = STPresetCameraType[uiData.camera.preset];
                if (uiData.camera.preset !== 'orthographicFront') {
                    sdkData.camera.rot = null;
                }
            }
            if (uiData.camera.rotation) {
                sdkData.camera.rot = {
                    lon: angleToEmu(uiData.camera.rotation.x ?? 0),
                    lat: angleToEmu(uiData.camera.rotation.y ?? 0),
                    rev: angleToEmu(uiData.camera.rotation.z ?? 0),
                };
            }
        }
        if (uiData.lightRig) {
            sdkData.lightRig = sdkData.lightRig ?? {};
            if (uiData.lightRig.rigPreset) {
                sdkData.lightRig.rig = STLightRigType[uiData.lightRig.rigPreset];
            }
            if (uiData.lightRig.rotation) {
                sdkData.lightRig.rot = {
                    lon: angleToEmu(uiData.lightRig.rotation.x ?? 0),
                    lat: angleToEmu(uiData.lightRig.rotation.y ?? 0),
                    rev: angleToEmu(uiData.lightRig.rotation.z ?? 0),
                };
            }
            if (uiData.lightRig.direction) {
                sdkData.lightRig.dir = STLightRigDirection[uiData.lightRig.direction];
            }
        }
    }

    public static convertShape3DFromUiToSdk(
        uiData?: DeepReadonly<DeepPartial<IShape3D>>,
        sdkData?: DeltaOf<ICTShape3D>,
    ) {
        if (!uiData || !sdkData) {
            return;
        }
        if (uiData.materialPreset) {
            sdkData.prstMaterial = STPresetMaterialType[uiData.materialPreset];
        }
        if (uiData.extrusionHeight) {
            sdkData.extrusionH = ptToEmu(uiData.extrusionHeight);
        }
        if (uiData.extrusionColor) {
            sdkData.extrusionClr = colorStrToColorDmlA(`#${uiData.extrusionColor.colorValue}`) as any;
        }
    }
}
